/**
 * How many CSS pixels make one PHYSICAL millimetre on the screen in front of the
 * reviewer, at the zoom level they are currently using.
 *
 * ECG paper only means anything at 1:1 — a major square has to measure 0.5 cm
 * under a ruler held to the glass, or the reviewer is reading a grid that lies
 * about the intervals it claims to show. Getting there needs the screen's real
 * pixel density, and that is where browsers stop helping.
 *
 * CSS defines absolute lengths as 96 px to the inch, so `1mm` in a stylesheet is
 * 96 / 25.4 px. On a desktop monitor at 100% zoom that is honest. On a phone it
 * is not, and not by a little: browsers pick `devicePixelRatio` to hold the CSS
 * px at a roughly constant ANGULAR size for a device held at reading distance,
 * not at a constant physical size. An iPhone 14 is 460 ppi at dpr 3, so its CSS
 * px density is 460 / 3 = 153 per inch, and a "millimetre" drawn as 96 / 25.4 px
 * comes out at 0.63 mm.
 *
 * No browser API reports physical DPI. `devicePixelRatio` is CSS px to device
 * px, `screen.width` is CSS px, and the `resolution` media feature restates
 * `devicePixelRatio` — none carries the panel's size in inches, and resolution
 * alone cannot supply it (1080 x 2400 ships in both 6.1" and 6.7" phones). So
 * the density here is ESTIMATED per device class, and a measured calibration
 * overrides the estimate when one has been stored.
 *
 * ---------------------------------------------------------------------------
 * Why the stored value is a HARDWARE density and not a px-per-mm
 * ---------------------------------------------------------------------------
 * Because zoom has to survive it. Writing the CSS px per millimetre straight to
 * storage would pin the calibration to the zoom level it was taken at, and every
 * ctrl-+ afterwards would inflate the grid: at 125% the major square would
 * measure 0.625 cm.
 *
 * What is actually invariant is the panel itself. Both browser zoom and the OS
 * display scale show up as factors in `devicePixelRatio`:
 *
 *     devicePixelRatio = zoom x osScale
 *     cssPxPerMm       = deviceDensity / devicePixelRatio
 *
 * where `deviceDensity` is the monitor's own device pixels per millimetre — a
 * property of the hardware that no zoom can move. Storing THAT and dividing by
 * the live `devicePixelRatio` on every read makes the grid hold 0.5 cm at any
 * zoom, and means a calibration can be taken at any zoom and stays correct at
 * every other one.
 *
 * The estimate cannot do this exactly: it guesses CSS px per inch, which already
 * has the zoom folded into it, and nothing can separate "this device is dense"
 * from "this reviewer is zoomed in". It anchors to the ratio seen when the app
 * loaded, so zooming after that at least preserves whatever accuracy it had.
 */

const MM_PER_INCH = 25.4;

/** CSS's own anchor. Exact for a desktop display at 100% zoom, and the fallback. */
export const CSS_PX_PER_INCH = 96;
export const CSS_PX_PER_MM = CSS_PX_PER_INCH / MM_PER_INCH;

/**
 * Estimated CSS px per inch for a phone.
 *
 * Derived from the current field: iPhone SE/11 sit at 163 (326 ppi / dpr 2),
 * iPhone 12-16 at 153 (460 / 3), Pixel 7-8 at 158-163 (416-428 / 2.625), Galaxy
 * S22-S23 at 142-143 (425-500 / 3-3.5). The band is 141-163 and 155 is its
 * middle, which holds every one of those within about 8%.
 */
const PHONE_CSS_PX_PER_INCH = 155;

/**
 * Estimated CSS px per inch for a tablet.
 *
 * Tablets are the weaker estimate: full-size iPads are 132 (264 ppi / dpr 2) and
 * Galaxy Tab S8 is 137, but an iPad mini is 163, and nothing observable
 * separates it from a full-size iPad — same dpr, same vendor. 140 sits with the
 * bulk of them and is roughly 15% low on a mini. A tablet used for measurement
 * is the case that most wants a stored calibration.
 */
const TABLET_CSS_PX_PER_INCH = 140;

/**
 * Short screen edge, in CSS px, below which a coarse-pointer device is a phone.
 *
 * Phones top out near 440 CSS px on their short edge (a 14 Pro Max is 430); the
 * smallest tablet is the iPad mini at 744. 500 sits in that gap with room on
 * both sides.
 */
const PHONE_SHORT_EDGE_PX = 500;

/** Where a measured calibration lives, as DEVICE pixels per physical millimetre. */
const CALIBRATION_KEY = "cardiolyse.ecg.deviceDensity";

/**
 * Sanity bounds on a stored density: 50 dpi to 1200 dpi, in device px per mm.
 *
 * Wide enough for anything from a projector to a 500 ppi phone panel. A corrupt
 * or hand-edited value outside it would render a grid so wrong it reads as a
 * broken page rather than as a miscalibration, so it is discarded in favour of
 * the estimate.
 */
const MIN_DENSITY = 50 / MM_PER_INCH;
const MAX_DENSITY = 1200 / MM_PER_INCH;

/**
 * Device pixels per CSS pixel, right now.
 *
 * Moves with browser zoom and with the OS display scale, which is precisely why
 * it is read live on every call rather than cached.
 */
export function devicePixelRatio(): number {
    if (typeof window === "undefined") {
        return 1
    }

    const ratio = window.devicePixelRatio;

    return isFinite(ratio) && ratio > 0 ? ratio : 1
}

/**
 * The ratio in force when the app loaded — the estimate's zoom anchor.
 *
 * The device-class numbers above describe a device at its NATIVE zoom, so they
 * are only valid at the ratio that zoom produces. Captured once here, so that a
 * reviewer who zooms mid-session keeps the physical scale they started with
 * instead of having the estimate silently re-interpret the zoom as density.
 * A reviewer who loads the page already zoomed gets the same error they would
 * have had with no anchoring at all — there is no signal that separates the two.
 */
const BASELINE_DEVICE_PIXEL_RATIO = devicePixelRatio();

/**
 * This screen's CSS px per millimetre, guessed from its device class.
 *
 * `pointer: coarse` is the same split the Modern skin's touch rules use — the
 * PRIMARY pointer, so a touchscreen laptop driven from its trackpad is treated
 * as the desktop it is being used as, and correctly keeps the exact 96.
 */
export function estimatePxPerMm(): number {
    if (typeof window === "undefined") {
        return CSS_PX_PER_MM
    }

    const coarse = typeof window.matchMedia === "function"
        && window.matchMedia("(pointer: coarse)").matches;

    let perInch = CSS_PX_PER_INCH;

    if (coarse) {
        // Orientation-independent: both axes are read and the smaller taken,
        // since iOS and Android disagree about whether `screen` follows a
        // rotation.
        const panel = window.screen;
        const shortEdge = panel ? Math.min(panel.width, panel.height) : 0;

        perInch = shortEdge > 0 && shortEdge < PHONE_SHORT_EDGE_PX
            ? PHONE_CSS_PX_PER_INCH
            : TABLET_CSS_PX_PER_INCH
    }

    const zoomSinceLoad = devicePixelRatio() / BASELINE_DEVICE_PIXEL_RATIO;

    return (perInch / MM_PER_INCH) / zoomSinceLoad
}

/**
 * The calibrated hardware density for this device, or null if there is none.
 *
 * localStorage is read defensively: Safari throws on access in Lockdown/private
 * modes rather than returning null, and an unreadable store is a device with no
 * calibration, not a broken ECG.
 */
export function readCalibratedDensity(): number | null {
    if (typeof window === "undefined") {
        return null
    }

    let stored: string | null = null;

    try {
        stored = window.localStorage.getItem(CALIBRATION_KEY)
    } catch (error) {
        return null
    }

    if (stored === null) {
        return null
    }

    const value = Number(stored);

    if (!isFinite(value) || value < MIN_DENSITY || value > MAX_DENSITY) {
        return null
    }

    return value
}

/**
 * Store this screen's hardware density, or clear it with null.
 *
 * Out-of-range values are rejected rather than clamped — a caller that computed
 * one has a bug upstream, and silently storing a clamped number would hide it
 * behind a grid that is merely a bit wrong.
 *
 * @param density device pixels per physical millimetre, or null to fall back to the estimate
 * @returns whether the value was accepted and persisted
 */
export function writeCalibratedDensity(density: number | null): boolean {
    if (typeof window === "undefined") {
        return false
    }

    if (density !== null && (!isFinite(density) || density < MIN_DENSITY || density > MAX_DENSITY)) {
        return false
    }

    try {
        if (density === null) {
            window.localStorage.removeItem(CALIBRATION_KEY)
        } else {
            window.localStorage.setItem(CALIBRATION_KEY, String(density))
        }
    } catch (error) {
        return false
    }

    return true
}

/**
 * Turn a px-per-mm the reviewer just matched by eye into a storable density.
 *
 * @param pxPerMm CSS px per millimetre, as measured at the CURRENT zoom
 */
export function densityFromPxPerMm(pxPerMm: number): number {
    return pxPerMm * devicePixelRatio()
}

/**
 * CSS pixels per millimetre of ECG paper, for drawing the grid at this zoom.
 *
 * A stored density wins over the estimate: it came from a ruler on this exact
 * screen, which is the one thing the browser could not tell us.
 */
export function getEcgPxPerMm(): number {
    const density = readCalibratedDensity();

    return density === null ? estimatePxPerMm() : density / devicePixelRatio()
}

/**
 * Run `listener` whenever `devicePixelRatio` changes — a zoom, or the window
 * being dragged to a differently-scaled display.
 *
 * `(resolution: Xdppx)` matches ONLY the ratio it names, so any change at all
 * breaks the match and fires. The query has to be re-armed each time, because
 * the new ratio needs a new query. `addListener` is the deprecated fallback for
 * Safari before 14, which has no `addEventListener` on a MediaQueryList.
 *
 * @param listener
 * @returns an unsubscribe function
 */
export function onDevicePixelRatioChange(listener: () => void): () => void {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return () => undefined
    }

    let media: MediaQueryList | null = null;
    let disposed = false;

    function detach() {
        if (media === null) {
            return
        }

        if (typeof media.removeEventListener === "function") {
            media.removeEventListener("change", handleChange)
        } else {
            media.removeListener(handleChange)
        }

        media = null
    }

    function attach() {
        media = window.matchMedia(`(resolution: ${devicePixelRatio()}dppx)`);

        if (typeof media.addEventListener === "function") {
            media.addEventListener("change", handleChange)
        } else {
            media.addListener(handleChange)
        }
    }

    function handleChange() {
        if (disposed) {
            return
        }

        detach();
        attach();
        listener()
    }

    attach();

    return () => {
        disposed = true;
        detach()
    }
}
